import { Flex } from 'antd'
import { useTranslation } from 'react-i18next'
import { formatNumber } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { LoadError } from '@/shared/ui/LoadError'
import { AmountCard } from './AmountCard'
import { AwaitingEndpoint } from './AwaitingEndpoint'
import { isEndpointMissing, netSpend } from './profileApi'
import type { CommissionState } from './useMyAccount'

/** Yig'indi endpointining manzili — ekranda ham, hisobotda ham bir xil */
export const COMMISSION_ENDPOINT = 'GET /api/agents/{id}/commission-summary'

interface SummaryCardsProps {
  state: CommissionState
  /** Endpoint hali yo'q bo'lganda nima ko'rsatilishi kerak edi */
  awaitingNote: string
}

/**
 * Davr bo'yicha to'liq manzara: amallar soni va UCH AJRATILGAN PUL SONI.
 *
 * <h2>⚠️ UCH SON BIR-BIRIGA QO'SHILMAYDI — egasi qarori (2026-08-19)</h2>
 * Komissiya to'ldirish summasidan yechilmaydi: depozitdan TO'LIQ summa
 * chiqadi, komissiya esa ALOHIDA tranzaksiya bilan depozitga qaytadi
 * (`02-LEDGER.md` §7.1, 2- va 3-qadamlar). Ya'ni sherik uchun uchta
 * boshqa-boshqa savol bor va ekran uchalasiga alohida javob beradi:
 *
 *   · «qancha to'ladim»    — `salesAmount`, depozitdan CHIQQANI;
 *   · «qanchasi qaytdi»    — `commission`, depozitga QAYTGAN cashback;
 *   · «sof qancha ketdi»   — birinchisi minus ikkinchisi ({@link netSpend}).
 *
 * Ularni bitta songa yig'ish («komissiya ayrilgan summa») aynan shu
 * so'rovni bekor qilardi — sherik cashback qaytganini KO'RA olmasdi.
 *
 * <h2>⚠️ MANBA — JURNAL, panel hisobi EMAS</h2>
 * Ikkala pul soni ham serverning `CommissionSummary` javobidan olinadi:
 * komissiya qoidasi vaqt o'tib o'zgaradi (`CommissionRule.validTo`) va
 * o'tmish davrni bugungi qoida bilan qayta hisoblash kassirning
 * qo'lidagi chekka mos kelmasdi. Uchinchi son ikkalasidan CHIQADI,
 * o'ylab topilmaydi.
 *
 * ⚠️ CASHBACK NOL BO'LISHI MUMKIN va bu HAQIQIY javob, «ma'lumot yo'q»
 * emas: nol komissiyada `CommissionServiceImpl.accrue` jurnalga umuman
 * yozuv qo'ymaydi. Bo'shlik faqat endpoint javob bermaganda aytiladi.
 *
 * ⚠️ «Amallar soni» — TO'LDIRISHLAR soni, paketlar soni emas: server
 * yig'indini `TOPUP` yozuvlaridan hisoblaydi. Yorliq shuni aynan
 * aytadi, aks holda paket bergan kassir sonni kam deb hisoblardi.
 */
export function SummaryCards({ state, awaitingNote }: SummaryCardsProps) {
  const { t } = useTranslation()

  if (state.error && isEndpointMissing(state.error)) {
    return <AwaitingEndpoint endpoint={COMMISSION_ENDPOINT} note={awaitingNote} />
  }
  if (state.error) {
    return <LoadError titleKey="profile.commissionFailed" error={state.error} />
  }

  const summary = state.summary
  // ⚠️ `null` — valyutalar mos kelmadi (`netSpend`), ya'ni son BOR emas.
  // Chiziqcha ko'rsatiladi; qolgan ikkitasi baribir to'g'ri.
  const net = summary ? netSpend(summary) : null

  return (
    <Flex gap={10} wrap>
      <AmountCard
        label={t('myOperations.salesCount')}
        value={summary ? formatNumber(summary.salesCount) : t('common.dash')}
        loading={state.loading}
      />
      <AmountCard
        label={t('myOperations.salesAmount')}
        hint={t('profile.toppedUpHint')}
        value={formatMoneyValue(summary?.salesAmount)}
        loading={state.loading}
      />
      <AmountCard
        label={t('profile.commission')}
        hint={t('profile.cashbackHint')}
        value={formatMoneyValue(summary?.commission)}
        loading={state.loading}
      />
      <AmountCard
        label={t('profile.netSpend')}
        hint={t('profile.netSpendHint')}
        value={formatMoneyValue(net)}
        loading={state.loading}
      />
    </Flex>
  )
}
